import React, { useState, useEffect } from 'react';
import { Mail, Search, Trash2, Phone, MessageSquare, Tag, Clock, CheckCircle, Circle, RefreshCw } from 'lucide-react';
import { getMessages, getMessagesAsync, markMessageAsRead, deleteMessage } from '../../services/adminStorage';

export default function MessagesManager() {
  const [messages, setMessages] = useState(getMessages());
  const [selectedMsgId, setSelectedMsgId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('todas'); // 'todas' | 'nao_lidas'
  const [isLoading, setIsLoading] = useState(false);

  const fetchMessages = async () => {
    setIsLoading(true);
    try {
      const data = await getMessagesAsync();
      setMessages(data);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    const handleUpdate = () => {
      setMessages(getMessages());
    };
    window.addEventListener('suzume_data_updated', handleUpdate);
    return () => window.removeEventListener('suzume_data_updated', handleUpdate);
  }, []);

  // Seleção e Marcação como Lida Automática
  const handleSelectMessage = (msg) => {
    setSelectedMsgId(msg.id);
    if (!msg.is_read) {
      markMessageAsRead(msg.id);
    }
  };

  const handleDeleteMessage = async (id) => {
    if (window.confirm('Tem certeza de que deseja excluir esta mensagem?')) {
      const updated = await deleteMessage(id);
      setMessages(updated);
      if (selectedMsgId === id) {
        setSelectedMsgId(null);
      }
    }
  };

  // Filtragem de mensagens
  const filteredMessages = messages.filter((msg) => {
    const matchFilter = filterType === 'todas' || (filterType === 'nao_lidas' && !msg.is_read);
    const query = searchQuery.toLowerCase().trim();
    const matchQuery = 
      !query ||
      (msg.name && msg.name.toLowerCase().includes(query)) ||
      (msg.email && msg.email.toLowerCase().includes(query)) ||
      (msg.phone && msg.phone.toLowerCase().includes(query)) ||
      (msg.category && msg.category.toLowerCase().includes(query)) ||
      (msg.message && msg.message.toLowerCase().includes(query));

    return matchFilter && matchQuery;
  });

  const selectedMessage = messages.find((m) => m.id === selectedMsgId);
  const unreadCount = messages.filter((m) => !m.is_read).length;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const d = new Date(dateString);
      return d.toLocaleString('pt-BR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="animate-fade-in">
      {/* Header Principal */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ fontSize: '1.75rem', color: 'var(--text-title)', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <Mail size={24} color="var(--color-brand-primary)" />
            Caixa de Entrada
            {unreadCount > 0 && (
              <span className="badge badge-brand" style={{ fontSize: '0.8rem', padding: '0.2rem 0.65rem' }}>
                {unreadCount} não lida{unreadCount > 1 ? 's' : ''}
              </span>
            )}
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>
            Mensagens enviadas pelos clientes através do formulário de contato do site.
          </p>
        </div>

        <button className="btn btn-secondary btn-sm" onClick={fetchMessages} disabled={isLoading}>
          <RefreshCw size={15} className={isLoading ? 'animate-spin' : ''} />
          Atualizar Mensagens
        </button>
      </div>

      {/* Interface Estilo Webmail (Master-Detail) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(320px, 380px) 1fr',
        gap: '1.5rem',
        minHeight: '650px',
        backgroundColor: '#ffffff',
        border: '1px solid var(--border-color)',
        borderRadius: '16px',
        boxShadow: 'var(--shadow-sm)',
        overflow: 'hidden'
      }}>

        {/* PAINEL ESQUERDO: Lista de Mensagens */}
        <div style={{ borderRight: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', backgroundColor: '#f8fafc' }}>
          
          {/* Barra de Busca e Filtros */}
          <div style={{ padding: '1.25rem', borderBottom: '1px solid var(--border-color)', backgroundColor: '#ffffff' }}>
            <div style={{ position: 'relative', marginBottom: '0.85rem' }}>
              <Search size={16} color="var(--text-muted)" style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)' }} />
              <input 
                type="text"
                placeholder="Buscar por nome, e-mail ou nicho..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.6rem 0.85rem 0.6rem 2.3rem',
                  fontSize: '0.85rem',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)',
                  outline: 'none'
                }}
              />
            </div>

            {/* Abas Filtros */}
            <div style={{ display: 'flex', gap: '0.35rem' }}>
              <button 
                onClick={() => setFilterType('todas')}
                style={{
                  flex: 1,
                  padding: '0.45rem',
                  fontSize: '0.8rem',
                  fontWeight: filterType === 'todas' ? 700 : 500,
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: filterType === 'todas' ? 'var(--color-brand-primary)' : 'transparent',
                  color: filterType === 'todas' ? '#ffffff' : 'var(--text-muted)',
                  cursor: 'pointer'
                }}
              >
                Todas ({messages.length})
              </button>
              <button 
                onClick={() => setFilterType('nao_lidas')}
                style={{
                  flex: 1,
                  padding: '0.45rem',
                  fontSize: '0.8rem',
                  fontWeight: filterType === 'nao_lidas' ? 700 : 500,
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: filterType === 'nao_lidas' ? 'var(--color-brand-primary)' : 'transparent',
                  color: filterType === 'nao_lidas' ? '#ffffff' : 'var(--text-muted)',
                  cursor: 'pointer'
                }}
              >
                Não Lidas ({unreadCount})
              </button>
            </div>
          </div>

          {/* Lista de Mensagens Recebidas */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {filteredMessages.length > 0 ? (
              filteredMessages.map((msg) => {
                const isSelected = msg.id === selectedMsgId;
                const isUnread = !msg.is_read;

                return (
                  <div
                    key={msg.id}
                    onClick={() => handleSelectMessage(msg)}
                    style={{
                      padding: '1.15rem 1.25rem',
                      borderBottom: '1px solid var(--border-color)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: isSelected ? '#eff6ff' : isUnread ? '#ffffff' : 'transparent',
                      borderLeft: isSelected ? '4px solid var(--color-brand-primary)' : isUnread ? '4px solid #3b82f6' : '4px solid transparent'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                      <span style={{ 
                        fontWeight: isUnread ? 800 : 600, 
                        color: isUnread ? 'var(--text-title)' : 'var(--text-body)',
                        fontSize: '0.92rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.4rem'
                      }}>
                        {isUnread ? <Circle size={8} fill="#3b82f6" color="#3b82f6" /> : <CheckCircle size={14} color="#94a3b8" />}
                        {msg.name}
                      </span>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        {formatDate(msg.created_at || msg.createdAt)}
                      </span>
                    </div>

                    <div style={{ marginBottom: '0.35rem' }}>
                      <span className="badge badge-category" style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem' }}>
                        {msg.category || 'Geral'}
                      </span>
                    </div>

                    <p style={{
                      fontSize: '0.82rem',
                      color: isUnread ? 'var(--text-title)' : 'var(--text-muted)',
                      fontWeight: isUnread ? 600 : 400,
                      margin: 0,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: '1.4'
                    }}>
                      {msg.message}
                    </p>
                  </div>
                );
              })
            ) : (
              <div style={{ padding: '3rem 1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Nenhuma mensagem encontrada nesta pasta.
              </div>
            )}
          </div>
        </div>

        {/* PAINEL DIREITO: Leitura Detalhada da Mensagem */}
        <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', backgroundColor: '#ffffff', overflowY: 'auto' }}>
          {selectedMessage ? (
            <div>
              {/* Header da Mensagem Aberta */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.75rem', paddingBottom: '1.5rem', borderBottom: '1px solid var(--border-color)' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <div style={{ width: '42px', height: '42px', borderRadius: '50%', backgroundColor: 'var(--color-brand-primary)', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '1.1rem' }}>
                      {selectedMessage.name ? selectedMessage.name.charAt(0).toUpperCase() : 'C'}
                    </div>
                    <div>
                      <h3 style={{ fontSize: '1.35rem', color: 'var(--text-title)', fontWeight: 800, margin: 0 }}>
                        {selectedMessage.name}
                      </h3>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.15rem' }}>
                        <Clock size={14} />
                        Recebido em {formatDate(selectedMessage.created_at || selectedMessage.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>

                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleDeleteMessage(selectedMessage.id)}
                  style={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.3)' }}
                  title="Excluir mensagem"
                >
                  <Trash2 size={16} />
                  <span>Excluir</span>
                </button>
              </div>

              {/* Grid de Informações de Contato */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                backgroundColor: 'var(--bg-main)',
                padding: '1.25rem',
                borderRadius: '12px',
                marginBottom: '2rem',
                border: '1px solid var(--border-color)'
              }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Mail size={14} color="var(--color-brand-primary)" /> E-mail
                  </div>
                  <a href={`mailto:${selectedMessage.email}`} style={{ fontSize: '0.9rem', color: 'var(--color-brand-primary)', fontWeight: 600, textDecoration: 'none' }}>
                    {selectedMessage.email}
                  </a>
                </div>

                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Phone size={14} color="var(--color-whatsapp)" /> WhatsApp / Telefone
                  </div>
                  <a 
                    href={`https://wa.me/55${selectedMessage.phone ? selectedMessage.phone.replace(/\D/g, '') : ''}`} 
                    target="_blank" 
                    rel="noreferrer"
                    style={{ fontSize: '0.9rem', color: 'var(--color-whatsapp)', fontWeight: 700, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                  >
                    <MessageSquare size={14} />
                    {selectedMessage.phone || 'Não informado'}
                  </a>
                </div>

                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Tag size={14} color="var(--color-warning)" /> Nicho de Interesse
                  </div>
                  <span className="badge badge-brand" style={{ fontSize: '0.8rem' }}>
                    {selectedMessage.category || 'Geral'}
                  </span>
                </div>
              </div>

              {/* Mensagem na Íntegral */}
              <div>
                <h4 style={{ fontSize: '1rem', color: 'var(--text-title)', fontWeight: 700, marginBottom: '0.75rem' }}>
                  Conteúdo da Mensagem:
                </h4>
                <div style={{
                  padding: '1.5rem',
                  backgroundColor: '#ffffff',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  color: 'var(--text-body)',
                  lineHeight: '1.7',
                  whiteSpace: 'pre-wrap',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
                }}>
                  {selectedMessage.message}
                </div>
              </div>

            </div>
          ) : (
            <div style={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justify: 'center', 
              color: 'var(--text-muted)',
              textAlign: 'center',
              padding: '3rem 1.5rem'
            }}>
              <Mail size={56} color="#cbd5e1" style={{ marginBottom: '1rem' }} />
              <h3 style={{ fontSize: '1.25rem', color: 'var(--text-title)', marginBottom: '0.5rem' }}>
                Nenhuma mensagem selecionada
              </h3>
              <p style={{ maxWidth: '360px', fontSize: '0.9rem' }}>
                Selecione uma mensagem da lista ao lado para visualizar os detalhes completos do cliente e o conteúdo enviado.
              </p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
